import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useEventListener from '@propertypal/shared/src/hooks/useEventListener';
import { AuctionsState } from '@propertypal/shared/src/reducers/auctions';
import { AuctionDate } from '@propertypal/shared/src/types/auction';
import { findFirstItemOutOfView, findItemInView } from '@propertypal/shared/src/utils/dom';
import throttle from 'lodash/throttle';
import React, { UIEventHandler, useEffect, useRef, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import { useTheme } from 'styled-components';
import { Text, TextProps } from '../typography';
import {
  ArrowLeft,
  ArrowRight,
  CalendarBox,
  CalendarContainer,
  CalendarDate,
  CalendarList,
  CalendarListItem,
  CalendarSlider,
  CalendarSliderBar,
  CalendarWrapper,
  Container,
} from './DateCalendar.style';

interface DateCalendarProps {
  dates: AuctionDate[];
  currentDate: AuctionsState['currentDate'];
  updateDate: (date: string) => void;
}

interface SliderStyle {
  width: number;
  transform: string;
}

const DEBOUNCE_TIME = process?.env?.JEST_WORKER_ID ? 0 : 300;

const DateCalendar: React.FC<DateCalendarProps> = ({ dates, currentDate, updateDate }) => {
  const [sliderStyles, setSliderStyles] = useState<SliderStyle | undefined>();
  const [activeDate, setActiveDate] = useState(currentDate);
  const [rightArrow, setRightArrow] = useState<boolean>(false);
  const [leftArrow, setLeftArrow] = useState<boolean>(false);

  const calendarRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const arrowRightRef = useRef<HTMLDivElement | null>(null);
  const arrowLeftRef = useRef<HTMLDivElement | null>(null);

  const refs = useRef(new Array(dates.length));

  const theme = useTheme();

  // Add smooth scroll polyfill for browsers not supporting scroll-behavior: smooth
  if (typeof window !== 'undefined') smoothscroll.polyfill();

  const dateTextProps: TextProps = {
    fontSize: 14,
    lineHeight: 16,
    align: 'center',
  };

  const selectDate = async (date: AuctionDate['value']) => {
    setActiveDate(date);
    updateDate(date);
  };

  /* handles moving the audi indicator about. */
  const transitionSlidyIndicator = () => {
    const rootBounds = boxRef && boxRef.current ? boxRef.current.getBoundingClientRect() : { left: 0 };

    let styles: SliderStyle | undefined;

    // refs are all sitting in an array at this point.
    // just need to loop, find the active one and set left and width props.
    for (let i = 0; i < refs.current.length; i += 1) {
      if (activeDate === dates[i].value) {
        const dateEl = refs.current[i] ? refs.current[i].getBoundingClientRect() : { width: 10 };
        styles = {
          width: dateEl.width,
          transform: `translate3d(${dateEl.left - rootBounds.left}px,0px,0px)`,
        };
        break;
      }
    }

    setSliderStyles(styles);
  };

  const dateArrows = () => {
    const lastElement = refs.current[refs.current.length - 1];
    const firstElement = refs.current[0];
    setRightArrow(findItemInView(lastElement));
    setLeftArrow(findItemInView(firstElement));
  };

  // scroll handler
  const handleSlidyNavScroll: UIEventHandler<HTMLElement> = () => {
    transitionSlidyIndicator();
    dateArrows();
  };

  const scrollLeft = () => {
    const windowWidth = window.innerWidth;
    const firstItemOutOfView = findFirstItemOutOfView(refs.current);

    let totalWidth = 0;
    const hiddenEls = refs.current.reverse().filter((el, index) => {
      const actualPos = refs.current.length - 1 - index;
      if (actualPos >= (firstItemOutOfView || 0)) return false;
      const elWidth = el.getBoundingClientRect().width;
      const combinedWidth = elWidth + totalWidth;
      if (combinedWidth > windowWidth) return false;
      totalWidth = combinedWidth;
      return true;
    });

    const targetEl = hiddenEls[hiddenEls.length - 1];

    if (sliderRef.current && targetEl) sliderRef.current.scrollTo({ behavior: 'smooth', left: targetEl.offsetLeft });
  };

  const scrollRight = () => {
    const subNavOffsetWidth = calendarRef.current ? calendarRef.current.clientWidth : -1;
    let nextEl;
    for (let i = 0; i < refs.current.length; i += 1) {
      const bounds = refs.current[i].getBoundingClientRect();
      if (bounds.right > subNavOffsetWidth) {
        nextEl = refs.current[i];
        break;
      }
    }
    if (sliderRef.current && 'scrollTo' in sliderRef.current)
      sliderRef.current.scrollTo({ behavior: 'smooth', left: nextEl ? nextEl.offsetLeft : 0 });
  };

  useEventListener(
    'resize',
    throttle(() => {
      dateArrows();
      transitionSlidyIndicator();
    }, DEBOUNCE_TIME),
  );

  useEffect(() => {
    transitionSlidyIndicator();
    dateArrows();
  }, [activeDate]);

  useEffect(() => {
    setActiveDate(currentDate);
  }, [currentDate]);

  return (
    <CalendarContainer ref={calendarRef} data-testid="date-calendar">
      <Container>
        <CalendarBox ref={boxRef}>
          <CalendarWrapper>
            <CalendarSlider ref={sliderRef} onScroll={handleSlidyNavScroll} data-testid="scroller">
              <CalendarList>
                {dates.map((d, index) => (
                  <CalendarListItem key={d.value}>
                    <CalendarDate
                      isActive={d.isActive}
                      ref={(el) => {
                        refs.current[index] = el;
                      }}
                      onFocus={() => setActiveDate(d.isActive ? d.value : currentDate)}
                      onBlur={() => setActiveDate(currentDate)}
                      onMouseEnter={() => setActiveDate(d.isActive ? d.value : currentDate)}
                      onMouseLeave={() => setActiveDate(currentDate)}
                      onClick={() => d.isActive && selectDate(d.value)}
                      data-testid="calendar-date"
                    >
                      <Text
                        {...dateTextProps}
                        textTransform="uppercase"
                        color={d.isActive ? theme.textLight : theme.backgroundLight}
                        fontWeight="600"
                        data-testid="day-of-week"
                      >
                        {d.dow}
                      </Text>
                      <Text
                        {...dateTextProps}
                        textTransform="inherit"
                        color={d.isActive ? theme.textDark : theme.backgroundLight}
                        fontWeight="bold"
                        data-testid="calendar-date-text"
                      >
                        {d.date}
                      </Text>
                    </CalendarDate>
                  </CalendarListItem>
                ))}
              </CalendarList>
            </CalendarSlider>
          </CalendarWrapper>
          <CalendarSliderBar
            width={sliderStyles?.width}
            transform={sliderStyles?.transform}
            data-testid="current-underline"
            data-current-date={activeDate}
          />
          {leftArrow ? (
            <ArrowLeft data-testid="arrowLeft" ref={arrowLeftRef} onClick={scrollLeft}>
              <FontAwesomeIcon icon={faChevronLeft} color={theme.primary} size="2x" />
            </ArrowLeft>
          ) : null}
          {rightArrow ? (
            <ArrowRight data-testid="arrowRight" ref={arrowRightRef} onClick={scrollRight}>
              <FontAwesomeIcon icon={faChevronRight} color={theme.primary} size="2x" />
            </ArrowRight>
          ) : null}
        </CalendarBox>
      </Container>
    </CalendarContainer>
  );
};

export default DateCalendar;
